import { useAuth0 } from '@auth0/auth0-react';
import { AnimatePresence } from 'framer-motion';
import { camelCase } from 'lodash';
import { useEffect, useLayoutEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import { StyledSplitWrapper } from './ReferralPanelPage.styles';
import referralsApi from '../../api/referrals';
import referrerApi from '../../api/referrer';
import Benefits from '../../components/Benefits';
import InviteALandlord from '../../components/InviteALandlord';
import Footer from '../../components/Layout/Footer';
import Header from '../../components/Layout/Header';
import Main from '../../components/Layout/Main';
import MyReferrals from '../../components/MyReferrals';
import Profile from '../../components/Profile';
import Backdrop from '../../components/UI/Backdrop';
import Divider from '../../components/UI/Divider';
import Welcome from '../../components/Welcome';
import { convertDateToString } from '../../helpers';
import { IReferral, IReferrer, TReferrals } from '../../types';

const ReferralPanelPage = (): JSX.Element => {
  const { isAuthenticated, loginWithRedirect, isLoading } = useAuth0();

  const { locale } = useParams();
  const [searchParams] = useSearchParams();

  const [isProfileVisible, setIsProfileVisible] = useState(false);
  const [referrer, setReferrer] = useState<IReferrer | undefined>();
  const [referrals, setReferrals] = useState<TReferrals | undefined>();

  const handleProfileBtnClick = () => {
    setIsProfileVisible(true);
  };

  const handleProfileClose = () => {
    setIsProfileVisible(false);
    fetchDataFromApi();
  };

  const fetchDataFromApi = async () => {
    try {
      const [{ data: referrerData }, { data: referralsData }] =
        await Promise.all([
          referrerApi.fetchReferrer(),
          referralsApi.fetchReferrals(),
        ]);

      if (!referrerData.utm_initialized) {
        await referrerApi.updateReferrerUTM({
          utm_source: searchParams.get('utm_source'),
          utm_medium: searchParams.get('utm_medium'),
          utm_campaign: searchParams.get('utm_campaign'),
        });
      }

      setReferrer({
        firstName: referrerData.first_name ?? null,
        lastName: referrerData.last_name ?? null,
        birthDate: referrerData.birth_date
          ? convertDateToString(new Date(referrerData.birth_date))
          : null,
        address: referrerData.address ?? null,
        email: referrerData.email,
        phoneNumber: referrerData.phone_no ?? null,
        iban: referrerData.iban ?? null,
        status: referrerData.status,
        invitationLink: referrerData.invitation_link,
        taxCode: referrerData.tax_code ?? null,
      } as IReferrer);

      setReferrals(
        referralsData.map(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (referral: any) =>
            ({
              name: referral.name,
              creationDate: convertDateToString(
                new Date(referral.creation_date),
              ),
              city: referral.city,
              status: camelCase(referral.status),
            } as IReferral),
        ),
      );
    } catch (e) {
      console.error(e);
    }
  };

  useLayoutEffect(() => {
    if (isProfileVisible) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = 'auto';
  }, [isProfileVisible]);

  useEffect(() => {
    if (isLoading) return;

    if (isAuthenticated) {
      if (window.location.search === '') {
        const utmSource = localStorage.getItem('utmSource') ?? '';
        const utmMedium = localStorage.getItem('utmMedium') ?? '';
        const utmCampaign = localStorage.getItem('utmCampaign') ?? '';

        localStorage.removeItem('utmSource');
        localStorage.removeItem('utmMedium');
        localStorage.removeItem('utmCampaign');

        window.location.search = `?utm_source=${utmSource}&utm_medium=${utmMedium}&utm_campaign=${utmCampaign}`;
      }

      fetchDataFromApi();
    } else {
      localStorage.setItem('utmSource', searchParams.get('utm_source') ?? '');
      localStorage.setItem('utmMedium', searchParams.get('utm_medium') ?? '');
      localStorage.setItem(
        'utmCampaign',
        searchParams.get('utm_campaign') ?? '',
      );

      loginWithRedirect({
        redirectUri: `${process.env.REACT_APP_ROOT_URL}/${locale}`,
      });
    }
  }, [isLoading, isAuthenticated]);

  return (
    <>
      {!isLoading && isAuthenticated && (
        <>
          <Header onProfileBtnClick={handleProfileBtnClick} />
          <Main>
            <AnimatePresence>
              {isProfileVisible && (
                <>
                  <Backdrop onClick={handleProfileClose} />
                  <Profile
                    profileData={referrer}
                    onCloseBtnClick={handleProfileClose}
                  />
                </>
              )}
            </AnimatePresence>
            <Welcome />
            <StyledSplitWrapper>
              <Benefits />
              <InviteALandlord
                invitationLink={referrer?.invitationLink}
                phoneNumber={referrer?.phoneNumber}
                taxCode={referrer?.taxCode}
                onPhoneNumberAndTaxCodeSaved={fetchDataFromApi}
              />
            </StyledSplitWrapper>
            {referrals && referrals.length > 0 && (
              <>
                <Divider />
                <MyReferrals referrals={referrals} />
              </>
            )}
          </Main>
          <Footer />
        </>
      )}
    </>
  );
};

export default ReferralPanelPage;
